import type { AvailabilityInfo } from "~/domains/product/api/pna/AvailabilityInfo";
import type { ProductData } from "~/domains/product/types/ProductData";
import { SalesHelper } from "~/domains/salesCampaign/utils/salesHelper";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import { useMemo } from "react";
import { splitBy } from "~/common/utils/reducers";

export class ProductHelper extends SalesHelper {
  private constructor(product: ProductData | null, availability: AvailabilityInfo | null) {
    super(product, availability);
  }

  // Don't use this directly, use useSalesHelper instead
  public static of(product: ProductDescriptor | undefined | null): ProductHelper {
    if (!product) {
      return new ProductHelper(null, null);
    }

    return new ProductHelper(product.base, product.availability ?? null);
  }

  // Selects smallest package, preferring those on sale first
  public selectBestPackage() {
    const packages = Object.values(this.product?.fifteenDigit ?? {});
    if (!packages?.length) {
      return null;
    }
    const [onSale, notOnSale] = splitBy(packages, (pack) => this.isPackageOnSale(pack.packCode));
    if (onSale.length > 0) {
      return onSale.reduce((acc, pack) => (pack.packSize < acc.packSize ? pack : acc));
    }
    return notOnSale.reduce((acc, pack) => (pack.packSize < acc.packSize ? pack : acc));
  }
}

export function useProductHelper(product: ProductDescriptor | undefined | null) {
  return useMemo(() => ProductHelper.of(product), [product]);
}
